import React, { useMemo, useState, useEffect } from "react";

const LogoMark = () => (
  <div className="flex items-center gap-2">
    <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#14b85a] text-white text-base font-bold">
      B
    </div>
    <span className="text-[20px] font-extrabold text-[#14b85a]">BUY iT</span>
  </div>
);

const PendingIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9" stroke="#111" strokeWidth="1.6" />
    <path d="M12 7v5l3 2" stroke="#111" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const UserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="8" r="4" stroke="#111" strokeWidth="1.6" />
    <path d="M5 20c1.8-3 5-5 7-5s5.2 2 7 5" stroke="#111" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const BikeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="6" cy="17" r="3" stroke="#111" strokeWidth="1.6" />
    <circle cx="18" cy="17" r="3" stroke="#111" strokeWidth="1.6" />
    <path d="M9 17h4l3-6h-4l-2-4H6" stroke="#111" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 17l3-6" stroke="#111" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const AllIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="4" width="7" height="7" rx="1" stroke="#111" strokeWidth="1.6" />
    <rect x="13" y="4" width="7" height="7" rx="1" stroke="#111" strokeWidth="1.6" />
    <rect x="4" y="13" width="7" height="7" rx="1" stroke="#111" strokeWidth="1.6" />
    <rect x="13" y="13" width="7" height="7" rx="1" stroke="#111" strokeWidth="1.6" />
  </svg>
);

const MoreIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="5" cy="12" r="2" fill="#111" />
    <circle cx="12" cy="12" r="2" fill="#111" />
    <circle cx="19" cy="12" r="2" fill="#111" />
  </svg>
);

const TopBar = () => (
  <div className="flex items-center justify-between border-b border-gray-200 bg-[#f3f4f4] px-6 py-4">
    <LogoMark />
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#14b85a] text-sm font-semibold text-white">
        J
      </div>
      <div className="text-right">
        <p className="text-sm font-semibold text-[#0b0b0b]">Good Afternoon, Jeff</p>
        <p className="text-xs text-gray-600">Admin</p>
      </div>
    </div>
  </div>
);

const SideNav = ({ activeTab, setActiveTab }) => {
  const items = [
    { label: "Pending", icon: <PendingIcon /> },
    { label: "Approved", icon: <UserIcon /> },
    { label: "Declined", icon: <BikeIcon /> },
    { label: "All", icon: <AllIcon /> },
    { label: "More", icon: <MoreIcon /> },
  ];

  return (
    <nav className="flex w-64 flex-col gap-2 bg-[#f3f4f4] px-4 py-6">
      {items.map((item, index) => {
        const isActive = activeTab === item.label;
        return (
          <div
            key={item.label}
            onClick={() => setActiveTab(item.label)}
            className={`flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-semibold transition ${
              isActive
                ? "bg-white text-[#14b85a] shadow-sm"
                : "text-[#0b0b0b] hover:bg-white"
            }`}
          >
            <span>{item.icon}</span>
            <span>{item.label}</span>
          </div>
        );
      })}
    </nav>
  );
};

const OrdersTable = ({ orders, onSelect }) => {
  if (!orders.length) {
    return (
      <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-600 shadow-sm">
        No orders in this section.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg bg-white shadow-sm">
      <table className="min-w-full text-left text-sm">
        <thead className="bg-[#f3f4f4] text-xs font-semibold uppercase text-gray-600">
          <tr>
            <th className="px-4 py-3">Buyer Name</th>
            <th className="px-4 py-3">Product Name</th>
            <th className="px-4 py-3">Product Price</th>
            <th className="px-4 py-3">Order Number</th>
            <th className="px-4 py-3">Time Left</th>
            <th className="px-4 py-3">Action</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr
              key={order.id}
              className="border-t border-gray-100 cursor-pointer hover:bg-gray-50"
              onClick={() => onSelect?.(order)}
            >
              <td className="px-4 py-3 font-medium text-[#0b0b0b]">{order.buyer}</td>
              <td className="px-4 py-3 text-gray-700">{order.productName}</td>
              <td className="px-4 py-3 text-gray-700">{order.productPrice}</td>
              <td className="px-4 py-3 text-gray-700">{order.orderNumber}</td>
              <td className="px-4 py-3 text-gray-700">{order.timeLeft}</td>
              <td className="px-4 py-3">
                <button
                  type="button"
                  className="rounded-md border border-[#14b85a] px-3 py-1 text-xs font-semibold text-[#14b85a] hover:bg-[#14b85a] hover:text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect?.(order);
                  }}
                >
                  View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AdminViewOrdersScreen = () => {
  const [activeTab, setActiveTab] = useState("Pending");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const statusQuery = useMemo(() => {
    if (activeTab === "All") return "";
    if (activeTab === "Approved") return "Confirmed Delivery";
    if (activeTab === "Declined") return "Declined";
    return activeTab;
  }, [activeTab]);

  const formatTimeLeft = (autoDeclineAt) => {
    if (!autoDeclineAt) return "N/A";
    const target = new Date(autoDeclineAt).getTime();
    const diffMs = Math.max(0, target - Date.now());
    const totalSeconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("adminToken");
        const url = statusQuery
          ? `${process.env.REACT_APP_API_URL}/api/admin/orders?status=${encodeURIComponent(statusQuery)}`
          : `${process.env.REACT_APP_API_URL}/api/admin/orders`;
        const res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        if (!data.success) {
          throw new Error(data.message || "Failed to load orders");
        }
        const normalized = (data.data || []).flatMap((order) =>
          (order.items || []).map((item, idx) => ({
            id: item.orderItemId || `${order._id}-${item.productId}-${idx}`,
            orderItemId: item.orderItemId,
            buyer: order.buyerFullName,
            productName: item.productName,
            productPrice: `NGN ${Number(item.price || 0).toLocaleString("en-NG")}`,
            orderNumber: item.orderNumber || "",
            timeLeft: formatTimeLeft(item.autoDeclineAt),
          }))
        );
        setOrders(normalized);
      } catch (err) {
        setError(err.message);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [statusQuery]);

  const filteredOrders = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return orders;
    return orders.filter((order) => (order.orderNumber || "").toLowerCase().includes(term));
  }, [orders, search]);

  return (
    <div className="min-h-screen w-full overflow-x-auto bg-[#f3f4f4] text-[#0b0b0b]">
      <div className="min-h-screen min-w-[600px]">
        <TopBar />
        <div className="flex">
          <SideNav activeTab={activeTab} setActiveTab={setActiveTab} />
          <main className="flex-1 px-6 py-6">
            <div className="mb-4">
              <h1 className="text-xl font-semibold text-[#0b0b0b]">Admin View Orders</h1>
              <p className="mt-1 text-sm text-gray-600">Monitor and review recent orders.</p>
            </div>
            <div className="mb-5">
              <div className="flex items-center gap-3 rounded-lg bg-white px-4 py-3 shadow-sm">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="11" cy="11" r="7" stroke="#111" strokeWidth="1.6" />
                  <path d="M16 16l4 4" stroke="#111" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by Order Number"
                  className="w-full text-sm text-[#0b0b0b] placeholder:text-gray-400 outline-none"
                />
              </div>
              <p className="mt-2 text-xs text-gray-500">Showing results for the order number you typed.</p>
            </div>
            {loading ? (
              <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-600 shadow-sm">
                Loading orders...
              </div>
            ) : error ? (
              <div className="rounded-lg bg-white p-8 text-center text-sm text-red-600 shadow-sm">
                {error}
              </div>
            ) : (
              <OrdersTable
                orders={filteredOrders}
                onSelect={(order) => {
                  if (!order?.orderItemId) return;
                  window.location.href = `/confirm-order-vendor-admin/${order.orderItemId}`;
                }}
              />
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminViewOrdersScreen;
